// features/cx/reconnection/api.ts

import { PaginatedResponse } from "@features/onboarding/utility-setup/types";
import { ApiEndpoints } from "@shared/api/api-endpoints";
import {
  cxApiClient,
  onboardingApiClient,
} from "@shared/api/clients/apiClientFactory";
import { DropdownItem } from "@shared/api/types";
import { transformJsonToObject } from "@shared/utils/jsonToObjectTransformer";
import {
  ConsumerMetaData,
  ConsumerSearchFilters,
  ConsumerSearchResult,
} from "../disconnect/types";
import {
  UtilityConfigFilters,
  UtilityRequestConfiguration,
} from "../shared/types/consumerRequest";
import {
  BulkReconnectionOperation,
  CreateReconnectionRequestPayload,
  Plan,
  ReconnectActivateConsumerPayload,
  ReconnectionFilters,
  ReconnectionRequest,
  ReconnectionSummary,
  UpdateConsumerPlanPayload,
  UpdateReconnectionRequestPayload,
} from "./types";

// API Functions
export const reconnectionApi = {
  searchConsumer: async (
    filters: ConsumerSearchFilters
  ): Promise<{ result: ConsumerSearchResult[] }> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "cx",
      "consumer-search",
      ["info"],
      (query) => {
        query.push("remote_utility_id", filters.remoteUtilityId.toString());
        query.push("search_data", filters.searchData);
        query.push("request_type", filters.requestType);
        return query;
      }
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: ConsumerSearchResult[] }>(
      response.data
    );
  },

  // Consumer Meta Data
  getConsumerMetaData: async (params: {
    consumerId: number;
    remoteUtilityId: number;
  }): Promise<{ result: ConsumerMetaData }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "consumer-meta-data",
      (query) => {
        query.push("consumer_id", params.consumerId.toString());
        query.push("remote_utility_id", params.remoteUtilityId.toString());
        return query;
      }
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: ConsumerMetaData }>(response.data);
  },

  // Reconnection Request List
  getReconnectionRequests: async (
    filters: ReconnectionFilters
  ): Promise<PaginatedResponse<ReconnectionRequest>> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "connection-status-request",
      (query) => {
        if (filters.remoteUtilityId)
          query.push("remote_utility_id", filters.remoteUtilityId.toString());
        if (filters.requestType)
          query.push("request_type", filters.requestType);
        if (filters.activeTab) query.push("active_tab", filters.activeTab);
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
        if (filters.searchData) query.push("search_data", filters.searchData);
        if (filters.status) query.push("status", filters.status);
        if (filters.priority) query.push("priority", filters.priority);
        return query;
      }
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<PaginatedResponse<ReconnectionRequest>>(
      response.data
    );
  },

  // Create Reconnection Request
  createReconnectionRequest: async (
    payload: CreateReconnectionRequestPayload
  ): Promise<ReconnectionRequest> => {
    const url = ApiEndpoints.createUrl("cx", "connection-status-request");
    const response = await cxApiClient.post(url, payload);
    return transformJsonToObject<ReconnectionRequest>(response.data);
  },

  // Get Reconnection Request Detail
  getReconnectionRequestDetail: async (
    requestId: number
  ): Promise<{ result: ReconnectionRequest }> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "cx",
      "connection-status-request",
      [requestId.toString()]
    );
    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: ReconnectionRequest }>(
      response.data
    );
  },

  // Update Reconnection Request
  updateReconnectionRequest: async (
    requestId: number,
    payload: UpdateReconnectionRequestPayload
  ): Promise<ReconnectionRequest> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "cx",
      "connection-status-request",
      [requestId.toString()]
    );
    const response = await cxApiClient.put(url, payload);
    return transformJsonToObject<ReconnectionRequest>(response.data);
  },

  // Delete Reconnection Request
  deleteReconnectionRequest: async (requestId: number): Promise<void> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "cx",
      "connection-status-request",
      [requestId.toString()]
    );
    await cxApiClient.delete(url);
  },

  // Get Plans (from onboarding API)
  getPlans: async (remoteUtilityId: string): Promise<Plan[]> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "plan",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("is_meter_required", 1);

        return query;
      }
    );

    const response = await onboardingApiClient.get(url);
    return transformJsonToObject<Plan[]>(response.data.result);
  },

  // Update Consumer Plan
  updateConsumerPlan: async (
    consumerId: number,
    payload: UpdateConsumerPlanPayload
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathVariables("cx", "consumer", [
      consumerId.toString(),
    ]);
    const response = await cxApiClient.put(url, payload);
    return response.data;
  },

  // Reconnect/Activate Consumer
  reconnectActivateConsumer: async (
    consumerId: string,
    payload: ReconnectActivateConsumerPayload
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "cx",
      "reconnect-activate-consumer",
      [consumerId]
    );
    const response = await cxApiClient.put(url, payload);
    return response.data;
  },

  // Bulk Operations
  bulkUpdateReconnectionRequests: async (
    operation: BulkReconnectionOperation
  ): Promise<any> => {
    const url = ApiEndpoints.createUrl(
      "cx",
      "connection-status-request/bulk-update"
    );
    const response = await cxApiClient.post(url, operation);
    return response.data;
  },

  // Status Change Operations
  approveReconnectionRequest: async (
    requestId: number,
    remarks?: string
  ): Promise<ReconnectionRequest> => {
    const payload: UpdateReconnectionRequestPayload = {
      status: 4, // Approved status
      consumerRemark: remarks,
    };
    return reconnectionApi.updateReconnectionRequest(requestId, payload);
  },

  rejectReconnectionRequest: async (
    requestId: number,
    remarks: string,
    closeDate: string
  ): Promise<ReconnectionRequest> => {
    const payload: UpdateReconnectionRequestPayload = {
      status: 3, // Rejected status
      consumerRemark: remarks,
      closeDate,
    };
    return reconnectionApi.updateReconnectionRequest(requestId, payload);
  },

  acknowledgeReconnectionRequest: async (
    requestId: number
  ): Promise<ReconnectionRequest> => {
    const payload: UpdateReconnectionRequestPayload = {
      status: 5, // Acknowledged status
    };
    return reconnectionApi.updateReconnectionRequest(requestId, payload);
  },

  holdReconnectionRequest: async (
    requestId: number,
    remarks?: string
  ): Promise<ReconnectionRequest> => {
    const payload: UpdateReconnectionRequestPayload = {
      status: 7, // On Hold status
      consumerRemark: remarks,
    };
    return reconnectionApi.updateReconnectionRequest(requestId, payload);
  },

  resolveReconnectionRequest: async (
    requestId: number,
    closeDate: string
  ): Promise<ReconnectionRequest> => {
    const payload: UpdateReconnectionRequestPayload = {
      status: 2, // Resolved status
      closeDate,
    };
    return reconnectionApi.updateReconnectionRequest(requestId, payload);
  },

  // Get Reconnection Summary
  getReconnectionSummary: async (params: {
    remoteUtilityId: number;
    requestType: string;
  }): Promise<ReconnectionSummary> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "connection-status-request-summary",
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId.toString());
        query.push("request_type", params.requestType);
        return query;
      }
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<ReconnectionSummary>(response.data.result);
  },

  // Complete Reconnection Workflow
  completeReconnectionWorkflow: async (params: {
    requestId: number;
    consumerId: number;
    planId?: number;
    meterData?: Record<string, any>;
  }): Promise<any> => {
    // Step 1: Approve the request
    await reconnectionApi.approveReconnectionRequest(params.requestId);

    // Step 2: Update consumer plan if provided
    if (params.planId) {
      await reconnectionApi.updateConsumerPlan(params.consumerId, {
        remotePlanId: params.planId,
      });
    }

    // Step 3: Reconnect/activate consumer with meter data if provided
    if (params.meterData) {
      await reconnectionApi.reconnectActivateConsumer(params.consumerId, {
        utilityServiceMeterData: params.meterData,
      });
    }

    // Step 4: Resolve the request
    return await reconnectionApi.resolveReconnectionRequest(
      params.requestId,
      new Date().toISOString().split("T")[0]
    );
  },

  getStatus: async (params: {
    remoteUtilityId: string;
  }): Promise<{ result: DropdownItem[] }> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "cx",
      "choices",
      ["consumer_support_request_status"],
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId.toString());
        return query;
      }
    );
    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: DropdownItem[] }>(response.data);
  },

  getCategory: async (params: {
    remoteUtilityId: string;
  }): Promise<{ result: DropdownItem[] }> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "cx",
      "choices",
      ["category"],
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId.toString());
        return query;
      }
    );
    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: DropdownItem[] }>(response.data);
  },

  getTimeSlotChoices: async (): Promise<{ result: DropdownItem[] }> => {
    const url = ApiEndpoints.createUrl("cx", "choices/time_slot");
    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: DropdownItem[] }>(response.data);
  },

  getUtilityRequestConfiguration: async (
    filters: UtilityConfigFilters
  ): Promise<{ result: UtilityRequestConfiguration[] }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "utility-request-configuration",
      (query) => {
        query.push("remote_utility_id", filters.remoteUtilityId.toString());
        query.push("request_type", filters.requestType);
        if (filters.disablePagination) query.push("disable_pagination", "true");
        if (filters.searchData) query.push("search_data", filters.searchData);
        return query;
      }
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<{ result: UtilityRequestConfiguration[] }>(
      response.data
    );
  },
  getMeterHistory: async (
    consumerId: number,
    remoteUtilityId: any
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "meter-history",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("consumer_id", consumerId);
        return query;
      }
    );
    const response = await cxApiClient.get(url);
    return response.data.result;
  },
};
